import React, { useEffect, useRef } from 'react';

/**
 * Pollen2D — guaranteed-visible pollen using plain Canvas 2D.
 * Works on every browser including iOS Safari low-power mode.
 * No WebGL, no CSS animations, just imperative drawing inside requestAnimationFrame.
 */
export default function Pollen2D({ count }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const parent = canvas.parentElement;

    const fit = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h };
    };
    let { w, h } = fit();

    const isMobile = window.innerWidth < 768;
    const N = typeof count === 'number' ? count : (isMobile ? 22 : 35);

    // particles state
    const ps = Array.from({ length: N }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: 2.5 + Math.random() * 4,                // 2.5–6.5 px
      vy: -(0.18 + Math.random() * 0.45),        // upward
      vx: (Math.random() - 0.5) * 0.18,
      a: 0.55 + Math.random() * 0.4,
      sway: Math.random() * Math.PI * 2,
      swaySpeed: 0.005 + Math.random() * 0.01,
    }));

    const onResize = () => {
      const f = fit(); w = f.w; h = f.h;
    };
    window.addEventListener('resize', onResize);

    let raf;
    let last = performance.now();
    let visible = true;
    const onVis = () => { visible = !document.hidden; if (visible) last = performance.now(); };
    document.addEventListener('visibilitychange', onVis);

    const tick = (t) => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      const dt = Math.min(50, t - last); last = t;

      ctx.clearRect(0, 0, w, h);

      for (const p of ps) {
        // motion
        p.sway += p.swaySpeed * dt;
        p.x += (p.vx + Math.sin(p.sway) * 0.12) * dt;
        p.y += p.vy * dt;
        // wrap to bottom when it exits the top
        if (p.y + p.r < -10) {
          p.y = h + 10;
          p.x = Math.random() * w;
        }
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;

        // draw soft glow + bright core
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
        grad.addColorStop(0, `rgba(255, 252, 230, ${p.a})`);
        grad.addColorStop(0.3, `rgba(255, 246, 200, ${p.a * 0.7})`);
        grad.addColorStop(1, 'rgba(255, 246, 200, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255, 255, 248, ${Math.min(1, p.a + 0.15)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="pollen-2d-canvas"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
}
